import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Animated, Easing, Pressable, StyleSheet, Text, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {useTranslation} from 'react-i18next';
import Icon from './SafeIcon';
import {IconChip} from '../premium/PremiumKit';
import {CapsuleBars} from '../premium/PremiumCharts';
import {useManagementTheme} from '../../helpers/managementTheme';

/* -------------------------------------------------------------------------- */
/*  Shared palette helpers                                                     */
/* -------------------------------------------------------------------------- */
export const CHART_PALETTE = ['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#F472B6', '#22D3EE', '#FB7185', '#A3E635'];

const clamp = (v, min = 0, max = 1) => Math.max(min, Math.min(max, v));

/* -------------------------------------------------------------------------- */
/*  Animated count-up number                                                   */
/* -------------------------------------------------------------------------- */
export const AnimatedNumber = ({value = 0, duration = 900, format, style}) => {
    const [display, setDisplay] = useState(0);
    const raf = useRef(null);
    const fromRef = useRef(0);

    useEffect(() => {
        const start = fromRef.current;
        const end = Number(value) || 0;
        const startTs = Date.now();
        const tick = () => {
            const t = clamp((Date.now() - startTs) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            const current = start + (end - start) * eased;
            setDisplay(current);
            if (t < 1) {
                raf.current = requestAnimationFrame(tick);
            } else {
                fromRef.current = end;
            }
        };
        raf.current = requestAnimationFrame(tick);
        return () => raf.current && cancelAnimationFrame(raf.current);
    }, [value, duration]);

    const text = format ? format(display) : String(Math.round(display));
    return <Text style={style} numberOfLines={1}>{text}</Text>;
};

/* -------------------------------------------------------------------------- */
/*  Trend badge (delta arrow + percentage)                                     */
/* -------------------------------------------------------------------------- */
export const TrendBadge = ({delta = 0, suffix = '%', neutralLabel}) => {
    const up = delta > 0;
    const flat = delta === 0;
    const color = flat ? '#94A3B8' : up ? '#10B981' : '#EF4444';
    const bg = flat ? 'rgba(148,163,184,0.16)' : up ? 'rgba(16,185,129,0.16)' : 'rgba(239,68,68,0.16)';
    const icon = flat ? 'trending-neutral' : up ? 'trending-up' : 'trending-down';
    return (
        <View style={[styles.trend, {backgroundColor: bg}]}>
            <Icon name={icon} size={13} color={color} />
            <Text style={[styles.trendText, {color}]}>
                {flat && neutralLabel ? neutralLabel : `${up ? '+' : ''}${Math.round(delta)}${suffix}`}
            </Text>
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Card wrapper for a chart                                                    */
/* -------------------------------------------------------------------------- */
export const ChartCard = ({icon, title, subtitle, right, children, tone = 'primary'}) => {
    const m = useManagementTheme();
    return (
        <View style={[styles.chartCard, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
            <View style={styles.chartHeader}>
                {!!icon && <IconChip icon={icon} tone={tone} size={38} round style={styles.chartHeaderIcon} />}
                <View style={{flex: 1}}>
                    <Text style={[styles.chartTitle, {color: m.colors.heading}]} numberOfLines={1}>{title}</Text>
                    {!!subtitle && <Text style={[styles.chartSubtitle, {color: m.colors.subText}]} numberOfLines={1}>{subtitle}</Text>}
                </View>
                {right}
            </View>
            {children}
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Interactive animated bar chart                                             */
/* -------------------------------------------------------------------------- */
export const BarChart = ({data = [], height = 160, valueFormat, onSelect}) => {
    const {t} = useTranslation();

    return (
        <CapsuleBars
            data={data}
            height={height}
            valueFormat={valueFormat}
            onSelect={onSelect}
            emptyText={t('mgmt_chart_no_period_data')}
        />
    );
};

/* -------------------------------------------------------------------------- */
/*  Sparkline (mini bars) for KPI cards                                         */
/* -------------------------------------------------------------------------- */
export const Sparkbars = ({data = [], color, height = 34, barCount = 12}) => {
    const m = useManagementTheme();
    const series = useMemo(() => {
        const values = data.map((d) => (typeof d === 'number' ? d : Number(d?.value) || 0));
        return values.slice(-barCount);
    }, [data, barCount]);
    const max = Math.max(1, ...series);
    return (
        <View style={[styles.spark, {height}]}>
            {series.map((v, i) => (
                <View
                    key={i}
                    style={{
                        flex: 1,
                        marginHorizontal: 1,
                        borderRadius: 3,
                        height: `${clamp(v / max) * 100}%`,
                        minHeight: 3,
                        backgroundColor: color || m.colors.primary,
                        opacity: 0.35 + (i / Math.max(1, series.length)) * 0.65,
                    }}
                />
            ))}
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Stacked segment bar (donut alternative) + legend                           */
/* -------------------------------------------------------------------------- */
export const SegmentBar = ({data = [], valueFormat, height = 16}) => {
    const m = useManagementTheme();
    const {t} = useTranslation();
    const items = useMemo(() => data.filter((d) => (Number(d.value) || 0) > 0), [data]);
    const total = useMemo(() => items.reduce((a, b) => a + (Number(b.value) || 0), 0), [items]);

    if (!total) return <EmptyChart text={t('mgmt_chart_no_distribution_data')} />;

    return (
        <View>
            <View style={[styles.segTrack, {height, backgroundColor: m.colors.surfaceAlt}]}>
                {items.map((d, i) => (
                    <View
                        key={`${d.label}-${i}`}
                        style={{
                            width: `${(Number(d.value) / total) * 100}%`,
                            backgroundColor: d.color || CHART_PALETTE[i % CHART_PALETTE.length],
                        }}
                    />
                ))}
            </View>
            <View style={styles.legend}>
                {items.map((d, i) => {
                    const pct = Math.round((Number(d.value) / total) * 100);
                    return (
                        <View key={`${d.label}-leg-${i}`} style={styles.legendItem}>
                            <View style={[styles.legendDot, {backgroundColor: d.color || CHART_PALETTE[i % CHART_PALETTE.length]}]} />
                            <View style={{flex: 1}}>
                                <Text style={[styles.legendLabel, {color: m.colors.text}]} numberOfLines={1}>{d.label}</Text>
                                <Text style={[styles.legendValue, {color: m.colors.subText}]} numberOfLines={1}>
                                    {valueFormat ? valueFormat(d.value) : d.value} · {pct}%
                                </Text>
                            </View>
                        </View>
                    );
                })}
            </View>
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Animated linear progress row                                               */
/* -------------------------------------------------------------------------- */
export const ProgressRow = ({label, value = 0, max = 100, tone = 'primary', valueText, last}) => {
    const m = useManagementTheme();
    const anim = useRef(new Animated.Value(0)).current;
    const pct = clamp(max > 0 ? value / max : 0);

    useEffect(() => {
        Animated.timing(anim, {toValue: pct, duration: 700, easing: Easing.out(Easing.cubic), useNativeDriver: false}).start();
    }, [pct, anim]);

    return (
        <View style={[styles.progressWrap, !last && {marginBottom: 14}]}>
            <View style={styles.progressTop}>
                <Text style={[styles.progressLabel, {color: m.colors.text}]} numberOfLines={1}>{label}</Text>
                <Text style={[styles.progressValue, {color: m.colors.heading}]}>{valueText != null ? valueText : `${Math.round(pct * 100)}%`}</Text>
            </View>
            <View style={[styles.progressTrack, {backgroundColor: m.colors.surfaceAlt}]}>
                <Animated.View style={{height: '100%', borderRadius: 999, overflow: 'hidden', width: anim.interpolate({inputRange: [0, 1], outputRange: ['0%', '100%']})}}>
                    <LinearGradient colors={m.toneGradient(tone)} start={{x: 0, y: 0}} end={{x: 1, y: 0}} style={StyleSheet.absoluteFill} />
                </Animated.View>
            </View>
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Circular progress ring (semicircle masking, no SVG dependency)             */
/* -------------------------------------------------------------------------- */
export const ProgressRing = ({size = 96, strokeWidth = 10, progress = 0, tone = 'primary', label, value}) => {
    const m = useManagementTheme();
    const pct = clamp(progress);
    const color = (m.toneGradient(tone) || [])[0] || m.colors.primary;
    const ring = {
        position: 'absolute',
        width: size,
        height: size,
        borderRadius: size / 2,
        borderWidth: strokeWidth,
        borderColor: color,
    };
    const rightDeg = pct <= 0.5 ? pct * 360 : 180;
    const leftDeg = pct <= 0.5 ? 0 : (pct - 0.5) * 360;

    const Half = ({side, deg}) => (
        <View style={[styles.ringHalf, {width: size / 2, height: size, left: side === 'right' ? size / 2 : 0}]}>
            <View style={[styles.ringHalfInner, {width: size / 2, height: size, left: side === 'right' ? -size / 2 : 0}]}>
                <View
                    style={[
                        ring,
                        {
                            left: side === 'right' ? 0 : -size / 2,
                            borderRightColor: 'transparent',
                            borderBottomColor: 'transparent',
                            transform: [{rotate: `${(side === 'right' ? 45 : 225) + deg}deg`}],
                        },
                    ]}
                />
            </View>
        </View>
    );

    return (
        <View style={{width: size, height: size, alignItems: 'center', justifyContent: 'center'}}>
            <View style={[ring, {borderColor: m.colors.surfaceAlt}]} />
            <Half side="right" deg={rightDeg} />
            <Half side="left" deg={leftDeg} />
            <View style={{alignItems: 'center'}}>
                <Text style={[styles.ringValue, {color: m.colors.heading}]}>{value != null ? value : `${Math.round(pct * 100)}%`}</Text>
                {!!label && <Text style={[styles.ringLabel, {color: m.colors.subText}]} numberOfLines={1}>{label}</Text>}
            </View>
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Premium KPI card with count-up + trend + sparkline                         */
/* -------------------------------------------------------------------------- */
export const KpiCard = ({icon, label, value, format, delta, series, tone = 'primary', width = '48%'}) => {
    const m = useManagementTheme();
    const sparkColor = (m.toneGradient(tone) || [])[0] || m.colors.primary;
    return (
        <View style={[styles.kpiCard, {width, backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
            <View style={styles.kpiTop}>
                <IconChip icon={icon} tone={tone} size={40} />
                {delta != null && <TrendBadge delta={delta} />}
            </View>
            <AnimatedNumber value={value} format={format} style={[styles.kpiValue, {color: m.colors.heading}]} />
            <Text style={[styles.kpiLabel, {color: m.colors.subText}]} numberOfLines={1}>{label}</Text>
            {!!series?.length && <View style={{marginTop: 10}}><Sparkbars data={series} color={sparkColor} /></View>}
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Business-area summary card (per placeType: restaurant/service/crm/erp...)   */
/* -------------------------------------------------------------------------- */
/**
 * Business-area summary card — gradient icon chip, bold title, one-line
 * description, a stat trio, an optional progress row and a soft circular
 * chevron affordance. Pressing scales the card down slightly (same tactile
 * feedback as the ModuleCard tiles); locked areas render greyed-out with the
 * reason under the subtitle.
 *
 * @param {Object} props - Component props.
 * @param {string} props.icon - MDI icon on the gradient chip.
 * @param {string} [props.tone='primary'] - Gradient tone for chip + progress.
 * @param {string} props.title - Bold card title.
 * @param {string} [props.subtitle] - Muted one-line description.
 * @param {Array<{label: string, value: string}>} [props.stats] - Stat trio under the header.
 * @param {Object} [props.progress] - ProgressRow props ({label, value, max, valueText}).
 * @param {Function} [props.onPress] - Tap handler (ignored when disabled).
 * @param {boolean} [props.disabled=false] - Render the locked state.
 * @param {string} [props.lockedReason] - Why the area is locked.
 * @returns {JSX.Element} The area summary card.
 */
export const AreaSummaryCard = ({icon, tone = 'primary', title, subtitle, stats = [], progress, onPress, disabled = false, lockedReason}) => {
    const m = useManagementTheme();
    return (
        <Pressable
            onPress={disabled ? undefined : onPress}
            disabled={disabled}
            style={({pressed}) => [
                styles.areaCard,
                {backgroundColor: m.colors.surface, borderColor: m.colors.border, opacity: disabled ? 0.52 : (pressed ? 0.9 : 1)},
                !disabled && pressed && {transform: [{scale: 0.98}]},
                m.shadow(1),
            ]}
        >
            <View style={styles.areaHeader}>
                <IconChip icon={disabled ? 'lock-outline' : icon} tone={disabled ? 'neutral' : tone} size={46} style={styles.areaIcon} />
                <View style={{flex: 1}}>
                    <Text style={[styles.areaTitle, {color: m.colors.heading}]} numberOfLines={1}>{title}</Text>
                    {!!subtitle && <Text style={[styles.areaSubtitle, {color: m.colors.subText}]} numberOfLines={1}>{subtitle}</Text>}
                    {disabled && !!lockedReason && <Text style={[styles.areaLocked, {color: m.colors.muted}]} numberOfLines={2}>{lockedReason}</Text>}
                </View>
                <View style={[styles.areaChevron, {backgroundColor: m.colors.surfaceAlt}]}>
                    <Icon name={disabled ? 'lock-outline' : 'chevron-right'} size={18} color={m.colors.muted} />
                </View>
            </View>
            <View style={styles.areaStats}>
                {stats.map((s, i) => (
                    <View key={`${s.label}-${i}`} style={styles.areaStat}>
                        <Text style={[styles.areaStatValue, {color: m.colors.heading}]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7}>{s.value}</Text>
                        <Text style={[styles.areaStatLabel, {color: m.colors.subText}]} numberOfLines={1}>{s.label}</Text>
                    </View>
                ))}
            </View>
            {!!progress && (
                <ProgressRow
                    label={progress.label}
                    value={progress.value}
                    max={progress.max}
                    tone={tone}
                    valueText={progress.valueText}
                    last
                />
            )}
        </Pressable>
    );
};

/* -------------------------------------------------------------------------- */
export const EmptyChart = ({text}) => {
    const m = useManagementTheme();
    const {t} = useTranslation();
    return (
        <View style={styles.emptyChart}>
            <Icon name="chart-line-variant" size={22} color={m.colors.muted} />
            <Text style={[styles.emptyChartText, {color: m.colors.subText}]}>{text || t('mgmt_chart_no_data')}</Text>
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Segmented period selector (7 / 30 / 90 days)                                */
/* -------------------------------------------------------------------------- */
export const SegmentedControl = ({options = [], value, onChange, disabled = false}) => {
    const m = useManagementTheme();
    return (
        <View style={[styles.segmented, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border, opacity: disabled ? 0.6 : 1}]}>
            {options.map((opt) => {
                const active = opt.value === value;
                return (
                    <Pressable key={opt.value} disabled={disabled} onPress={() => onChange?.(opt.value)} style={styles.segmentedBtn}>
                        {active ? (
                            <LinearGradient colors={m.toneGradient('primary')} start={{x: 0, y: 0}} end={{x: 1, y: 0}} style={styles.segmentedActive}>
                                <Text style={styles.segmentedActiveText}>{opt.label}</Text>
                            </LinearGradient>
                        ) : (
                            <Text style={[styles.segmentedText, {color: m.colors.subText}]}>{opt.label}</Text>
                        )}
                    </Pressable>
                );
            })}
        </View>
    );
};

/* -------------------------------------------------------------------------- */
const styles = StyleSheet.create({
    trend: {flexDirection: 'row', alignItems: 'center', gap: 3, borderRadius: 999, paddingHorizontal: 8, paddingVertical: 3},
    trendText: {fontSize: 11.5, fontWeight: '800'},

    chartCard: {borderRadius: 26, borderWidth: 1, padding: 18, marginBottom: 14},
    chartHeader: {flexDirection: 'row', alignItems: 'center', marginBottom: 16},
    chartHeaderIcon: {marginRight: 12},
    chartTitle: {fontSize: 16.5, fontWeight: '800', letterSpacing: -0.2},
    chartSubtitle: {fontSize: 12, fontWeight: '600', marginTop: 1},

    barTopRow: {marginBottom: 10},
    barActiveValue: {fontSize: 22, fontWeight: '900'},
    barActiveLabel: {fontSize: 12.5, fontWeight: '700', marginTop: 1},
    barArea: {flexDirection: 'row', alignItems: 'flex-end'},
    barColumn: {flex: 1, alignItems: 'center', height: '100%', justifyContent: 'flex-end'},
    barTrack: {flex: 1, width: '100%', alignItems: 'center', justifyContent: 'flex-end'},
    barLabel: {fontSize: 10.5, fontWeight: '700', marginTop: 6},

    spark: {flexDirection: 'row', alignItems: 'flex-end'},

    segTrack: {flexDirection: 'row', borderRadius: 999, overflow: 'hidden'},
    legend: {marginTop: 14, flexDirection: 'row', flexWrap: 'wrap'},
    legendItem: {flexDirection: 'row', alignItems: 'center', width: '50%', paddingVertical: 6, paddingRight: 8},
    legendDot: {width: 10, height: 10, borderRadius: 5, marginRight: 8},
    legendLabel: {fontSize: 12.5, fontWeight: '800'},
    legendValue: {fontSize: 11.5, fontWeight: '600', marginTop: 1},

    progressWrap: {},
    progressTop: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7},
    progressLabel: {fontSize: 13, fontWeight: '700', flex: 1, paddingRight: 8},
    progressValue: {fontSize: 13, fontWeight: '900'},
    progressTrack: {height: 10, borderRadius: 999, overflow: 'hidden'},

    ringHalf: {position: 'absolute', top: 0, overflow: 'hidden'},
    ringHalfInner: {position: 'absolute', top: 0, overflow: 'hidden'},
    ringValue: {fontSize: 19, fontWeight: '900'},
    ringLabel: {fontSize: 11, fontWeight: '700', marginTop: 1},

    kpiCard: {borderRadius: 24, borderWidth: 1, padding: 16},
    kpiTop: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12},
    kpiIcon: {width: 38, height: 38, borderRadius: 12, alignItems: 'center', justifyContent: 'center'},
    kpiValue: {fontSize: 21, fontWeight: '900'},
    kpiLabel: {fontSize: 12, fontWeight: '600', marginTop: 2},

    areaCard: {borderRadius: 24, borderWidth: 1, padding: 16, marginBottom: 12},
    areaHeader: {flexDirection: 'row', alignItems: 'center', marginBottom: 14},
    areaIcon: {marginRight: 13},
    areaTitle: {fontSize: 16, fontWeight: '800', letterSpacing: -0.2},
    areaSubtitle: {fontSize: 12, fontWeight: '600', marginTop: 1},
    areaLocked: {fontSize: 11, fontWeight: '800', marginTop: 4, lineHeight: 15},
    areaChevron: {width: 30, height: 30, borderRadius: 999, alignItems: 'center', justifyContent: 'center', marginLeft: 8},
    areaStats: {flexDirection: 'row', flexWrap: 'wrap', marginBottom: 4},
    areaStat: {width: '33.33%', paddingVertical: 4},
    areaStatValue: {fontSize: 18, fontWeight: '800', letterSpacing: -0.3},
    areaStatLabel: {fontSize: 11, fontWeight: '600', marginTop: 1},

    emptyChart: {alignItems: 'center', justifyContent: 'center', paddingVertical: 24, gap: 8},
    emptyChartText: {fontSize: 12.5, fontWeight: '600', textAlign: 'center'},

    segmented: {flexDirection: 'row', borderRadius: 999, borderWidth: 1, padding: 3},
    segmentedBtn: {flex: 1, alignItems: 'center', justifyContent: 'center'},
    segmentedActive: {width: '100%', alignItems: 'center', borderRadius: 999, paddingVertical: 7},
    segmentedActiveText: {color: '#FFFFFF', fontSize: 12.5, fontWeight: '800'},
    segmentedText: {fontSize: 12.5, fontWeight: '800', paddingVertical: 7},
});



